import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import SEOHead from './components/SEOHead';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import AgentsPage from './pages/AgentsPage';
import CampaignsPage from './pages/CampaignsPage';
import CallHistoryPage from './pages/CallHistoryPage';
import AnalyticsPage from './pages/AnalyticsPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import SettingsPage from './pages/SettingsPage';
import IntegrationsPage from './pages/IntegrationsPage';
import PhoneNumbersPage from './pages/PhoneNumbersPage';
import EcommercePage from './pages/EcommercePage';
import OnCallLeadsPage from './pages/OnCallLeadsPage';
import PricingPage from './pages/PricingPage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/policies/PrivacyPolicyPage';
import TermsPage from './pages/policies/TermsPage';
import DisclaimerPage from './pages/policies/DisclaimerPage';
import RefundPolicyPage from './pages/policies/RefundPolicyPage';
import ShippingPolicyPage from './pages/policies/ShippingPolicyPage';
import DashboardLayout from './components/DashboardLayout';
import AdminLayout from './components/AdminLayout';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminOverviewPage from './pages/admin/AdminOverviewPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminApiKeysPage from './pages/admin/AdminApiKeysPage';
import AdminDemoCallsPage from './pages/admin/AdminDemoCallsPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminInvestorPage from './pages/admin/AdminInvestorPage';
import AdminShowcasesPage from './pages/admin/AdminShowcasesPage';
import AdminContactsPage from './pages/admin/AdminContactsPage';
import AdminLeadsPage from './pages/admin/AdminLeadsPage';
import AdminBlogPage from './pages/admin/AdminBlogPage';
import AdminSeoPage from './pages/admin/AdminSeoPage';
import BlogListPage from './pages/BlogListPage';
import BlogPostPage from './pages/BlogPostPage';
import DotCallChatbot from './components/DotCallChatbot';
import { Loader2 } from 'lucide-react';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#020617' }}>
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#020617' }}>
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  const path = location.pathname;
  // Show chatbot only on public pages (hide on /dashboard and /admin*)
  const isPrivateArea = path.startsWith('/dashboard') || path.startsWith('/admin');

  return (
    <>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/policies/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/policies/terms-conditions" element={<TermsPage />} />
        <Route path="/policies/disclaimer" element={<DisclaimerPage />} />
        <Route path="/policies/refund-policy" element={<RefundPolicyPage />} />
        <Route path="/policies/shipping-policy" element={<ShippingPolicyPage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
          <Route index element={<DashboardPage />} />
          <Route path="agents" element={<AgentsPage />} />
          <Route path="campaigns" element={<CampaignsPage />} />
          <Route path="calls" element={<CallHistoryPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="knowledge-base" element={<KnowledgeBasePage />} />
          <Route path="integrations" element={<IntegrationsPage />} />
          <Route path="phone-numbers" element={<PhoneNumbersPage />} />
          <Route path="ecommerce" element={<EcommercePage />} />
          <Route path="oncall-leads" element={<OnCallLeadsPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>
        {/* Admin Routes */}
        <Route path="/admin" element={<AdminLoginPage />} />
        <Route path="/admin/dashboard" element={<AdminLayout />}>
          <Route index element={<AdminOverviewPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="api-keys" element={<AdminApiKeysPage />} />
          <Route path="demo-calls" element={<AdminDemoCallsPage />} />
          <Route path="settings" element={<AdminSettingsPage />} />
          <Route path="investors" element={<AdminInvestorPage />} />
          <Route path="showcases" element={<AdminShowcasesPage />} />
          <Route path="contact-submissions" element={<AdminContactsPage />} />
          <Route path="chatbot-leads" element={<AdminLeadsPage />} />
          <Route path="blog" element={<AdminBlogPage />} />
          <Route path="seo" element={<AdminSeoPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!isPrivateArea && <DotCallChatbot />}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <SEOHead />
        <AppRoutes />
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
